import { SearchBox } from '@/components';
import {
  Dropdown,
  DropdownCheckboxItem,
  DropdownContent,
  DropdownEmpty,
  DropdownLoading,
  DropDownSelectToClose,
  DropdownTriggerButton,
} from '@/components/Dropdown';
import { CheckboxCell, CheckboxHeader, TextCell } from '@/components/tables/contentFields';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import TableLoadingView from '@/components/tables/TableLoadingView';
import { useDeviceModels } from '@/features/device-models';
import useDebounce from '@/utils/useDebounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessibleCompanies, useAssignableDevices, useAssignableDevicesCount } from '../hooks';
import type { AssignableDevice, AssignableDeviceQueryParams } from '../types';
import AssignmentContentContainer from './assignmentContainer';

interface SelectDevicesStepProps {
  userCompanyId: string;
  selectedDeviceIds: Set<string>;
  selectedDevices: Map<string, AssignableDevice>;
  onSelectionChange: (ids: Set<string>, devices: Map<string, AssignableDevice>) => void;
  selectedTargetCompanyId: string | null;
  onSourceCompanyChange: (companyId: string) => void;
  sourceCompanyId: string;
}

export function SelectDevicesStep({
  userCompanyId,
  selectedDeviceIds,
  selectedDevices,
  onSelectionChange,
  selectedTargetCompanyId,
  onSourceCompanyChange,
  sourceCompanyId,
}: SelectDevicesStepProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [selectedModelTypes, setSelectedModelTypes] = useState<number[]>([]);

  const debouncedSearch = useDebounce(searchInput);

  const { data: accessibleCompanies, isLoading: isLoadingCompanies } = useAccessibleCompanies(userCompanyId);
  const { data: deviceTypes, isLoading: isLoadingDeviceTypes } = useDeviceModels();

  const queryParams: AssignableDeviceQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      companyId: sourceCompanyId,
      modelTypeIds: selectedModelTypes.length > 0 ? selectedModelTypes : undefined,
    }),
    [page, pageSize, debouncedSearch, sourceCompanyId, selectedModelTypes]
  );

  const countParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      companyId: sourceCompanyId,
      modelTypeIds: selectedModelTypes.length > 0 ? selectedModelTypes : undefined,
    }),
    [debouncedSearch, sourceCompanyId, selectedModelTypes]
  );

  const { data: devicesData, isLoading: isLoadingDevices, isFetching } = useAssignableDevices(
    queryParams,
    !!sourceCompanyId
  );
  const { data: countData } = useAssignableDevicesCount(countParams, !!sourceCompanyId);


  const devices = devicesData?.data || [];
  const total = countData?.total || 0;

  const deviceTypeMap = useMemo(() => {
    if (!deviceTypes) return new Map<number, string>();
    return new Map(deviceTypes.map((dt) => [dt.id, dt.name]));
  }, [deviceTypes]);

  const handleCompanyChange = useCallback(
    (companyId: string) => {
      onSourceCompanyChange(companyId);
      setPage(1);
      onSelectionChange(new Set(), new Map());
    },
    [onSourceCompanyChange, onSelectionChange]
  );

  const handleModelTypeChange = useCallback(
    (modelTypeId: number, checked: boolean) => {
      const newModelTypes = checked
        ? [...selectedModelTypes, modelTypeId]
        : selectedModelTypes.filter((id) => id !== modelTypeId);
      setSelectedModelTypes(newModelTypes);
      setPage(1);
      onSelectionChange(new Set(), new Map());
    },
    [selectedModelTypes, onSelectionChange]
  );

  useEffect(() => {
    if (sourceCompanyId === selectedTargetCompanyId && selectedTargetCompanyId) {
      // This will be handled by the parent component
    }
  }, [sourceCompanyId, selectedTargetCompanyId]);

  const allSelected = devices.length > 0 && devices.every((d) => selectedDeviceIds.has(d.id));
  const someSelected = devices.some((d) => selectedDeviceIds.has(d.id)) && !allSelected;

  const handleSelectAll = useCallback(() => {
    const newIds = new Set(selectedDeviceIds);
    const newDevices = new Map(selectedDevices);

    if (allSelected) {
      devices.forEach((d) => {
        newIds.delete(d.id);
        newDevices.delete(d.id);
      });
    } else {
      devices.forEach((d) => {
        newIds.add(d.id);
        newDevices.set(d.id, d);
      });
    }

    onSelectionChange(newIds, newDevices);
  }, [allSelected, devices, selectedDeviceIds, selectedDevices, onSelectionChange]);

  const handleSelectRow = useCallback(
    (device: AssignableDevice) => {
      const newIds = new Set(selectedDeviceIds);
      const newDevices = new Map(selectedDevices);

      if (newIds.has(device.id)) {
        newIds.delete(device.id);
        newDevices.delete(device.id);
      } else {
        newIds.add(device.id);
        newDevices.set(device.id, device);
      }

      onSelectionChange(newIds, newDevices);
    },
    [selectedDeviceIds, selectedDevices, onSelectionChange]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const selectedCompanyName = useMemo(() => {
    if (!accessibleCompanies) return '';
    const company = accessibleCompanies.find((c) => c.id === sourceCompanyId);
    return company?.name || '';
  }, [accessibleCompanies, sourceCompanyId]);

  return (
    <AssignmentContentContainer
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    >
      <div className="flex flex-wrap gap-2">
        {/* Company Filter */}
        <Dropdown>
          <DropdownTriggerButton className="min-w-[200px] justify-between">
            <span className="truncate">{selectedCompanyName || t('deviceAssignment.step1.selectCompany')}</span>
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </DropdownTriggerButton>
          <DropdownContent>
            {isLoadingCompanies ? (
              <DropdownLoading rows={3} />
            ) : !accessibleCompanies || accessibleCompanies.length === 0 ? (
              <DropdownEmpty>{t('deviceAssignment.step1.noCompanies')}</DropdownEmpty>
            ) : (
              accessibleCompanies.map((company) => (
                <DropDownSelectToClose key={company.id} doSelect={() => handleCompanyChange(company.id)}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-x-3 py-1.5 px-2 rounded-lg text-[13px] font-normal hover:bg-surface-darker ${sourceCompanyId === company.id ? 'text-primary' : 'text-onsurface'
                      }`}
                  >
                    {company.name}
                  </button>
                </DropDownSelectToClose>
              ))
            )}
          </DropdownContent>
        </Dropdown>

        {/* Model Type Filter */}
        <Dropdown>
          <DropdownTriggerButton>
            {t('deviceAssignment.step1.modelFilter')}
            {selectedModelTypes.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                {selectedModelTypes.length}
              </span>
            )}
            <svg
              className="shrink-0 size-3.5"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </DropdownTriggerButton>
          <DropdownContent>
            {isLoadingDeviceTypes ? (
              <DropdownLoading rows={3} />
            ) : !deviceTypes || deviceTypes.length === 0 ? (
              <DropdownEmpty>{t('deviceAssignment.step1.noModels')}</DropdownEmpty>
            ) : (
              deviceTypes.map((type) => (
                <DropdownCheckboxItem
                  key={type.id}
                  checked={selectedModelTypes.includes(type.id)}
                  onChange={(checked) => handleModelTypeChange(type.id, checked)}
                >
                  {type.name}
                </DropdownCheckboxItem>
              ))
            )}
          </DropdownContent>
        </Dropdown>

        {/* Search */}
        <SearchBox
          className="relative flex-1 min-w-[200px]"
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('deviceAssignment.step1.searchPlaceholder')}
          ariaLabel={t('deviceAssignment.step1.searchPlaceholder')}
          clearAriaLabel={t('deviceAssignment.step1.searchPlaceholder')}
        />
      </div>

      {/* Table */}
      <div className="relative grow min-h-0">
        {isFetching && !isLoadingDevices && (
          <div className="absolute top-2 right-2 z-10">
            <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}

        {isLoadingDevices ? (
          <TableLoadingView isLoading={true} rows={5} columns={3} withHeader={true} />
        ) : devices.length === 0 ? (
          <EmptyTableView />
        ) : (
          <Table className="min-h-0 h-full">
            <TableHeader>
              <CheckboxHeader
                checked={allSelected}
                onChange={handleSelectAll}
                ariaLabel={t('deviceAssignment.step1.selectAll')}
                indeterminate={someSelected}
              />
              <NonSortableColumnHeader label={t('deviceAssignment.step1.serialNumber')} minWidth="150px" />
              <NonSortableColumnHeader label={t('deviceAssignment.step1.model')} minWidth="150px" />
            </TableHeader>
            <TableBody className="divide-y divide-border">
              {devices.map((device) => (
                <tr key={device.id} className="hover:bg-surface-darker/50">
                  <CheckboxCell
                    checked={selectedDeviceIds.has(device.id)}
                    onChange={() => handleSelectRow(device)}
                    ariaLabel={t('deviceAssignment.step1.selectDevice', { serial: device.serialNumber })}
                  />
                  <TextCell value={device.serialNumber} />
                  <TextCell value={deviceTypeMap.get(Number(device.modelTypeId)) || device.modelTypeId} />
                </tr>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </AssignmentContentContainer>
  );
}
